import React, { useEffect, useState } from 'react'
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap'

import { useCartRedux, useUserRedux, useAuth, useMembershipRedux, useOrderRedux, useCartTotals } from '@hooks'
import { Loader } from '@components'
import CartContent from './CartContent'
import CartHeader from './CartHeader'
import CartFooter from './CartFooter'

const CartModal = ({ isOpen, onOpen, onClose }) => {
    const { items, creditToUseCents, resetCredit } = useCartRedux()
    const { diningMethod } = useUserRedux()
    const { isAuthenticated } = useAuth()
    const { getCurrentMember, isLoading } = useMembershipRedux()
    const { fetchAvailablePromos, updatePromoCheckTimestamp } = useOrderRedux()

    // Use hook for cart totals (memoized selectors)
    const { totals } = useCartTotals()

    const [animate, setAnimate] = useState(false)
    const [isRefetching, setIsRefetching] = useState(false)
    const isShowingLoading = isOpen && isRefetching && isLoading

    // Effects - Animation
    useEffect(() => {
        if (items.length > 0) {
            setAnimate(true)
            setTimeout(() => setAnimate(false), 500)
        }
    }, [items.length])

    // Effects - Reset credit when modal closes
    useEffect(() => {
        if (!isOpen && creditToUseCents > 0) {
            resetCredit()
        }
    }, [isOpen, creditToUseCents, resetCredit])

    // Effects - Refetch member data when modal opens
    useEffect(() => {
        if (isOpen && isAuthenticated) {
            setIsRefetching(true)
            getCurrentMember().finally(() => {
                setIsRefetching(false)
            })
        } else {
            // Reset refetching state when modal closes
            setIsRefetching(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isAuthenticated, getCurrentMember])

    // Effects - Fetch promos and trigger promo recalculation when cart modal opens
    useEffect(() => {
        if (isOpen) {
            fetchAvailablePromos()
            // Update timestamp to force promo eligibility recalculation
            // This ensures time-based promos are checked against current time
            updatePromoCheckTimestamp()
        }
    }, [isOpen, fetchAvailablePromos, updatePromoCheckTimestamp])

    const renderDiningText = () => {
        // Display dining method text
        const methodLabel = diningMethod?.label || diningMethod?.value || 'Dine-in'
        return methodLabel
    }

    if (!items.length) {
        return null
    }

    return (
        <>
            <Button className={`cart-summary-button ${animate ? 'cart-animate' : ''}`} onClick={onOpen} color="white">
                <div className="cart-summary-row">
                    <div className="cart-summary-title">Cart</div>
                    <div className="cart-summary-amount">${totals.finalAmount.toFixed(2)}</div>
                </div>
                <div className="cart-summary-row">
                    <div className="cart-summary-subtitle">
                        {totals.count} {totals.count === 1 ? 'item' : 'items'}
                    </div>
                    <div className="cart-summary-meta">{renderDiningText()}</div>
                </div>
            </Button>

            <Modal isOpen={isOpen} toggle={onClose} fullscreen="lg" centered>
                {isShowingLoading && <Loader />}
                <ModalHeader toggle={onClose} className="custom-modal-header">
                    Cart Items
                </ModalHeader>
                <ModalBody className="cart-modal-body">
                    <CartHeader />
                    <div className="hide-scrollbar">
                        <CartContent />
                    </div>
                </ModalBody>
                <CartFooter />
            </Modal>
        </>
    )
}

export default CartModal
